import React from "react";
import { useUnsavedChangesGuard } from "../../hooks/useUnsavedChanges";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Dropdown } from "primereact/dropdown";
import { Checkbox } from "primereact/checkbox";
import { Calendar } from "primereact/calendar";
import { MultiSelect } from "primereact/multiselect";
import { Button } from "primereact/button";

import {
  Benefit,
  BenefitCtaMode,
  BenefitDiscountType,
  BenefitInput,
  BenefitRedemptionMethod,
  BenefitStatus,
} from "../../models/benefits";
import {
  benefitCtaModeOptions,
  benefitDiscountTypeOptions,
  benefitRedemptionMethodOptions,
  benefitStatusOptions,
  benefitValidDayOptions,
  resolveBenefitCtaMode,
} from "../../utils/constants/benefits-constants";
import {
  useCreateBenefitMutation,
  useGetBenefitPartnersQuery,
  useUpdateBenefitMutation,
} from "../../store/services/benefits";
import { endOfDay } from "date-fns";
import { useTenantScope } from "../../hooks/useTenantScope";
import { ChoiceCards, FormActions, FormField, FormRow, FormSection, FormSections, emptyMessage, useTenantGate } from "../ui";
import { yupConstants } from "../../utils/constants/yup";
import { getAdminFacingApiErrorMessage } from "../../utils/functions/api-errors";
import { uploadImageToS3 } from "../../utils/functions/uploadToS3";
import { BenefitImagesManager } from "./BenefitImagesManager";
import { CALENDAR_LOCALE } from "../../utils/constants/calendar-constants";

const ALLOWED_IMAGE_TYPES = ["image/png", "image/jpeg", "image/webp"];
const MAX_IMAGE_BYTES = 5 * 1024 * 1024;

const validateImageFile = (file: File): string | null => {
  if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
    return "Formato de imagen no soportado. Usá PNG, JPG o WebP.";
  }
  if (file.size > MAX_IMAGE_BYTES) {
    return "La imagen supera el máximo de 5 MB.";
  }
  return null;
};

interface BenefitFormProps {
  /** Presente en modo edición. */
  benefitData?: Benefit;
}

interface BenefitFormValues {
  partnerId: string;
  title: string;
  description: string;
  discountType: BenefitDiscountType | null;
  discountValue: string;
  discountLabel: string;
  promoCode: string;
  ctaMode: BenefitCtaMode;
  ctaLabel: string;
  ctaInfoText: string;
  redemptionMethods: BenefitRedemptionMethod[];
  termsAndConditions: string;
  startDate: Date | null;
  endDate: Date | null;
  status: BenefitStatus;
  priority: number;
  validDays: number[];
  validHoursStart: Date | null;
  validHoursEnd: Date | null;
  eligibleRoles: number[];
  images: File[];
}

const parseBenefitDate = (value?: string | null): Date | null => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

/** "HH:MM" -> Date (solo hora/minuto) para el Calendar timeOnly. */
const parseBenefitTime = (value?: string | null): Date | null => {
  if (!value) return null;
  const match = /^(\d{1,2}):(\d{2})$/.exec(value.trim());
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) return null;
  const date = new Date();
  date.setHours(hours, minutes, 0, 0);
  return date;
};

/** Date -> "HH:MM". */
const formatBenefitTime = (value: Date | null): string | null => {
  if (!value) return null;
  const hours = String(value.getHours()).padStart(2, "0");
  const minutes = String(value.getMinutes()).padStart(2, "0");
  return `${hours}:${minutes}`;
};

export const BenefitForm = ({ benefitData }: BenefitFormProps) => {
  const navigate = useNavigate();
  const isEdit = Boolean(benefitData);

  const tenantScope = useTenantScope();

  // MT-011 / §6.4 — Tenant sobre el que opera la pantalla:
  // - EDICIÓN: el tenant propio del beneficio (no se puede mover de tenant).
  // - ALTA: el del selector global del header. El formulario ya NO tiene su
  //   propio dropdown de tenant: INC-05 lo prohíbe y lo hereda del header.
  //   Si el header está en "Todos", `tenantGate` bloquea la creación.
  const tenantGate = useTenantGate();
  const effectiveTenantId = isEdit
    ? benefitData?.tenantId ?? null
    : tenantScope.tenantId;

  const { data: partners, isLoading: isLoadingPartners } =
    useGetBenefitPartnersQuery(
      { tenantId: effectiveTenantId },
      { skip: !effectiveTenantId }
    );

  const [createBenefit, { isLoading: isCreating }] = useCreateBenefitMutation();
  const [updateBenefit, { isLoading: isUpdating }] = useUpdateBenefitMutation();

  const isSubmitting = isCreating || isUpdating;

  // Imagen de canje (opcional). Mismo flujo que el logo del partner: se mantiene
  // un File pendiente y se sube via presigned al hacer submit; al backend viaja
  // solo la URL pública resultante.
  const [redemptionFile, setRedemptionFile] = React.useState<File | null>(null);
  // Quitar la imagen de canje ya guardada (en edición): viaja como null al
  // guardar para que el backend la borre. Antes solo se podía reemplazar.
  const [removeSavedRedemptionImage, setRemoveSavedRedemptionImage] =
    React.useState(false);
  const savedRedemptionImageUrl = removeSavedRedemptionImage
    ? null
    : benefitData?.redemptionImageUrl ?? null;
  const redemptionInputRef = React.useRef<HTMLInputElement>(null);

  const redemptionPreviewUrl = React.useMemo(
    () => (redemptionFile ? URL.createObjectURL(redemptionFile) : null),
    [redemptionFile]
  );

  React.useEffect(() => {
    return () => {
      if (redemptionPreviewUrl) URL.revokeObjectURL(redemptionPreviewUrl);
    };
  }, [redemptionPreviewUrl]);

  const handleFileSelect =
    (setFile: React.Dispatch<React.SetStateAction<File | null>>) =>
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;
        const fileError = validateImageFile(file);
        if (fileError) {
          toast.error(fileError);
          return;
        }
        setFile(file);
      };

  const redemptionPreviewSrc = redemptionPreviewUrl || savedRedemptionImageUrl || "";

  const partnerOptions = React.useMemo(
    () =>
      (partners ?? []).map((partner) => ({
        label: partner.name ?? partner.id,
        value: partner.id,
      })),
    [partners]
  );

  const {
    handleSubmit,
    touched,
    errors,
    values,
    handleBlur,
    handleChange,
    setFieldValue,
    dirty,
  } = useFormik<BenefitFormValues>({
    // Sin enableReinitialize: los refetch por mutaciones de imágenes no deben
    // pisar los cambios sin guardar del form (el manager sincroniza lo suyo).
    initialValues: {
      partnerId: benefitData?.partnerId || "",
      title: benefitData?.title || "",
      description: benefitData?.description || "",
      discountType: benefitData?.discountType ?? null,
      discountValue: benefitData?.discountValue || "",
      discountLabel: benefitData?.discountLabel || "",
      promoCode: benefitData?.promoCode || "",
      // Con cta_mode NULL (beneficios viejos) se deduce igual que en la app.
      ctaMode: resolveBenefitCtaMode(benefitData) ?? "redemption",
      // En creación arranca en modo "redemption" (default de arriba), por eso
      // la etiqueta trae un default: "Usar" y no "Canjear", que en la app se
      // confundía con el canje de Puntos. En edición se respeta lo guardado.
      ctaLabel: benefitData?.ctaLabel || (isEdit ? "" : "Usar"),
      ctaInfoText: benefitData?.ctaInfoText || "",
      redemptionMethods: benefitData?.redemptionMethods ?? [],
      termsAndConditions: benefitData?.termsAndConditions || "",
      startDate: parseBenefitDate(benefitData?.startDate),
      endDate: parseBenefitDate(benefitData?.endDate),
      status: benefitData?.status || "draft",
      priority: benefitData?.priority ?? 0,
      validDays: benefitData?.validDays ?? [],
      validHoursStart: parseBenefitTime(benefitData?.validHoursStart),
      validHoursEnd: parseBenefitTime(benefitData?.validHoursEnd),
      eligibleRoles: benefitData?.eligibleRoles ?? [],
      images: [],
    },
    validationSchema: Yup.object({
      partnerId: Yup.string().required(yupConstants().required),
      title: Yup.string().required(yupConstants().required),
      ctaInfoText: Yup.string().when("ctaMode", {
        is: "info_text",
        then: (schema) =>
          schema.required("Requerido cuando el CTA es texto informativo"),
        otherwise: (schema) => schema.notRequired(),
      }),
      endDate: Yup.date()
        .nullable()
        .test(
          "end-after-start",
          "La fecha de fin debe ser igual o posterior a la de inicio",
          function (value) {
            const { startDate } = this.parent as BenefitFormValues;
            if (!value || !startDate) return true;
            return value.getTime() >= startDate.getTime();
          }
        ),
      validHoursEnd: Yup.date()
        .nullable()
        .test(
          "hours-end-after-start",
          "El horario hasta debe ser posterior al horario desde",
          function (value) {
            const { validHoursStart } = this.parent as BenefitFormValues;
            if (!value || !validHoursStart) return true;
            const start =
              validHoursStart.getHours() * 60 + validHoursStart.getMinutes();
            const end = value.getHours() * 60 + value.getMinutes();
            // Desde = hasta deja el beneficio válido un solo minuto (el backend
            // compara la hora con los dos bordes inclusive).
            return end > start;
          }
        ),
      // Con el método "Código promocional" la app muestra el código al canjear:
      // sin código, el miembro veía el canje vacío.
      promoCode: Yup.string().when(["ctaMode", "redemptionMethods"], {
        is: (ctaMode: BenefitCtaMode, methods: BenefitRedemptionMethod[] | undefined) =>
          ctaMode === "redemption" && Boolean(methods?.includes("promo_code")),
        then: (schema) =>
          schema.trim().required("Requerido con el método Código promocional"),
        otherwise: (schema) => schema,
      }),
      redemptionMethods: Yup.array().when("ctaMode", {
        is: "redemption",
        then: (schema) =>
          schema.min(1, "Seleccioná al menos un método de canje"),
        otherwise: (schema) => schema,
      }),
      images: Yup.array().test(
        "at-least-one-image",
        "Subí al menos una imagen",
        (value) => (benefitData?.images?.length ?? 0) + (value?.length ?? 0) > 0
      ),
    }),
    onSubmit: async (formValues) => {
      // En update los campos de texto viajan siempre (permite vaciarlos);
      // en create se omiten los vacíos.
      const textField = (value: string): string | undefined =>
        isEdit ? value : value || undefined;
      // Campos opcionales que no son texto: vacíos se omiten al crear, pero al
      // editar viajan como null para que el backend los borre. Si se omitieran,
      // el update conservaría el valor anterior y no habría forma de vaciarlos.
      const emptyValue = isEdit ? null : undefined;
      const listField = <T,>(list: T[]): T[] | null | undefined =>
        list.length > 0 ? list : emptyValue;

      // Las imágenes se suben desde el navegador via presigned URL (mismo flujo que
      // eventos): al backend viajan solo las URLs, en el orden elegido por el usuario.
      // La imagen de canje sigue el mismo flujo (subir → enviar URL).
      let imageUrls: string[] | undefined;
      let redemptionImageUrl: string | undefined;
      try {
        [imageUrls, redemptionImageUrl] = await Promise.all([
          formValues.images.length > 0
            ? Promise.all(
              formValues.images.map((file) =>
                uploadImageToS3(file, "benefits", file.name)
              )
            )
            : Promise.resolve(undefined),
          redemptionFile
            ? uploadImageToS3(redemptionFile, "benefits", redemptionFile.name)
            : Promise.resolve(undefined),
        ]);
      } catch {
        toast.error("No se pudieron subir las imágenes. Probá de nuevo.");
        return;
      }

      const input: BenefitInput = {
        title: formValues.title,
        description: textField(formValues.description),
        discountType: formValues.discountType ?? emptyValue,
        // "Valor" solo existe para el tipo Porcentaje (el campo se oculta con
        // los demás): con otro tipo se vacía para no dejar un número viejo
        // colgado, que el listado mostraba como "2x1 · 10".
        discountValue:
          formValues.discountType === "percentage"
            ? textField(formValues.discountValue)
            : textField(""),
        discountLabel: textField(formValues.discountLabel),
        promoCode: textField(formValues.promoCode),
        ctaMode: formValues.ctaMode,
        ctaLabel: textField(formValues.ctaLabel),
        ctaInfoText:
          formValues.ctaMode === "info_text"
            ? formValues.ctaInfoText
            : textField(formValues.ctaInfoText),
        termsAndConditions: textField(formValues.termsAndConditions),
        // En "Texto informativo" no hay canje: si quedaban métodos de antes, la
        // app seguía mostrando sus chips y el badge del listado.
        redemptionMethods:
          formValues.ctaMode === "redemption" ? formValues.redemptionMethods : [],
        redemptionImageUrl:
          redemptionImageUrl ??
          (isEdit && removeSavedRedemptionImage && benefitData?.redemptionImageUrl
            ? null
            : undefined),
        startDate: formValues.startDate
          ? formValues.startDate.toISOString()
          : emptyValue,
        // Fin de vigencia inclusive: el beneficio vale TODO ese día. A las 00:00
        // el backend ya lo daba por vencido el mismo día que el BO mostraba
        // como último.
        endDate: formValues.endDate
          ? endOfDay(formValues.endDate).toISOString()
          : emptyValue,
        status: formValues.status,
        priority: formValues.priority,
        validDays: listField(formValues.validDays),
        validHoursStart: formatBenefitTime(formValues.validHoursStart) ?? emptyValue,
        validHoursEnd: formatBenefitTime(formValues.validHoursEnd) ?? emptyValue,
        eligibleRoles: listField(formValues.eligibleRoles),
        imageUrls,
      };

      try {
        if (isEdit && benefitData) {
          // La API no permite cambiar el partner ni el tenant en update: el
          // tenant del beneficio solo viaja como header para operar sobre él.
          await updateBenefit({
            id: benefitData.id,
            contextTenantId: benefitData.tenantId,
            ...input,
          }).unwrap();
          toast.success("Beneficio actualizado");
        } else {
          await createBenefit({
            ...input,
            partnerId: formValues.partnerId,
            tenantId:
              !tenantScope.hidden && effectiveTenantId
                ? effectiveTenantId
                : undefined,
            contextTenantId: effectiveTenantId,
          }).unwrap();
          toast.success("Beneficio creado");
        }
        navigate("/benefits-catalog");
      } catch (error) {
        toast.error(
          getAdminFacingApiErrorMessage(
            error,
            isEdit
              ? "No se pudo actualizar el beneficio"
              : "No se pudo crear el beneficio"
          )
        );
      }
    },
  });

  // Avisa antes de que un cambio de tenant en el header recargue la pantalla
  // y se lleve puesto lo que se estaba cargando.
  useUnsavedChangesGuard(
    dirty || redemptionFile !== null || removeSavedRedemptionImage
  );

  // Al cambiar el tenant destino, el partner elegido puede ser de otro tenant:
  // se limpia para no mandar una combinación que el backend rechaza.
  React.useEffect(() => {
    if (
      !isEdit &&
      partners &&
      values.partnerId &&
      !partners.some((partner) => partner.id === values.partnerId)
    ) {
      setFieldValue("partnerId", "");
    }
  }, [isEdit, partners, values.partnerId, setFieldValue]);

  const toggleRedemptionMethod = (
    method: BenefitRedemptionMethod,
    checked: boolean
  ) => {
    const next = checked
      ? [...values.redemptionMethods, method]
      : values.redemptionMethods.filter((value) => value !== method);
    setFieldValue("redemptionMethods", next);
  };

  const errorText = (field: keyof BenefitFormValues): string | undefined =>
    touched[field] && errors[field] ? (errors[field] as string) : undefined;

  return (
    <form onSubmit={handleSubmit}>
      <FormSections>
      {/* ---- Información general ---- */}
      {/* §5 — etiqueta arriba del campo, grilla de 3 columnas iguales en todas
          las secciones para que los campos queden alineados y simétricos. Las
          aclaraciones van en la etiqueta, el placeholder o la introducción de
          la sección: nunca texto suelto debajo del campo (solo errores). */}
      <FormSection title="Información general">
        <FormRow columns={3}>
          <FormField
            label={isEdit ? "Partner (no se puede cambiar)" : "Partner"}
            htmlFor="partnerId"
            error={errorText("partnerId")}
          >
            <Dropdown
              invalid={!!errorText("partnerId")}
              className="w-full"
              inputId="partnerId"
              name="partnerId"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.partnerId}
              optionLabel="label"
              optionValue="value"
              options={partnerOptions}
              loading={isLoadingPartners}
              disabled={isSubmitting || isEdit || !effectiveTenantId}
              filter
              placeholder="Seleccionar partner"
              emptyMessage={emptyMessage("partners")}
            />
          </FormField>
          <FormField label="Título" htmlFor="title" error={errorText("title")}>
            <InputText
              invalid={!!errorText("title")}
              className="w-full"
              id="title"
              name="title"
              placeholder="Ej: 20% off en toda la carta"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.title}
              disabled={isSubmitting}
            />
          </FormField>
          <FormField label="Estado" htmlFor="status">
            <Dropdown
              className="w-full"
              inputId="status"
              name="status"
              placeholder="Seleccionar estado"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.status}
              optionLabel="label"
              optionValue="value"
              options={benefitStatusOptions}
              disabled={isSubmitting}
            />
          </FormField>
          <FormField label="Descripción" htmlFor="description" span="full">
            <InputTextarea
              className="w-full"
              id="description"
              name="description"
              placeholder="Contá en qué consiste el beneficio"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.description}
              rows={4}
              autoResize
              disabled={isSubmitting}
            />
          </FormField>
        </FormRow>
      </FormSection>

      {/* ---- Descuento ---- */}
      <FormSection title="Descuento">
        <FormRow columns={values.discountType === "percentage" ? 3 : 2}>
          <FormField label="Tipo de descuento" htmlFor="discountType">
            <Dropdown
              className="w-full"
              inputId="discountType"
              name="discountType"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.discountType}
              optionLabel="label"
              optionValue="value"
              options={benefitDiscountTypeOptions}
              disabled={isSubmitting}
              showClear
              placeholder="Seleccionar tipo"
            />
          </FormField>
          {values.discountType === "percentage" && (
            <FormField label="Valor (%)" htmlFor="discountValue">
              <InputText
                className="w-full"
                id="discountValue"
                name="discountValue"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.discountValue}
                placeholder="Ej: 20"
                disabled={isSubmitting}
              />
            </FormField>
          )}
          <FormField label="Etiqueta que se muestra en la app" htmlFor="discountLabel">
            <InputText
              className="w-full"
              id="discountLabel"
              name="discountLabel"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.discountLabel}
              placeholder="Ej: 20% OFF"
              disabled={isSubmitting}
            />
          </FormField>
        </FormRow>
      </FormSection>

      {/* ---- CTA y canje ---- */}
      {/* El modo va en tarjetas con una línea que explica qué ve el miembro, y
          debajo solo los campos de ese modo: en "Texto informativo" la app no
          muestra botón ni canje, así que esos campos no aplican (sus valores se
          conservan si se vuelve a "Canje"). */}
      <FormSection title="CTA y canje">
        <FormRow columns={2}>
          <FormField label="Modo de CTA" span="full">
            <ChoiceCards
              name="ctaMode"
              options={benefitCtaModeOptions}
              value={values.ctaMode}
              onChange={(mode) => void setFieldValue("ctaMode", mode)}
              disabled={isSubmitting}
            />
          </FormField>

          {values.ctaMode === "info_text" ? (
            <FormField
              label="Texto informativo"
              htmlFor="ctaInfoText"
              error={errorText("ctaInfoText")}
              span="full"
            >
              <InputTextarea
                className={`w-full ${errorText("ctaInfoText") ? "p-invalid" : ""}`}
                id="ctaInfoText"
                name="ctaInfoText"
                placeholder="Ej: Mostrá tu credencial en caja para obtener el descuento"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.ctaInfoText}
                rows={3}
                autoResize
                disabled={isSubmitting}
              />
            </FormField>
          ) : (
            <>
              <FormField
                label="Métodos de canje"
                error={errorText("redemptionMethods")}
                span="full"
              >
                <div className="flex flex-wrap gap-3 py-2">
                  {benefitRedemptionMethodOptions.map((option) => (
                    <div key={option.value} className="flex align-items-center gap-2">
                      <Checkbox
                        inputId={`redemption-${option.value}`}
                        checked={values.redemptionMethods.includes(option.value)}
                        onChange={(e) =>
                          toggleRedemptionMethod(option.value, Boolean(e.checked))
                        }
                        disabled={isSubmitting}
                      />
                      <label htmlFor={`redemption-${option.value}`}>{option.label}</label>
                    </div>
                  ))}
                </div>
              </FormField>
              <FormField label="Texto del botón" htmlFor="ctaLabel">
                <InputText
                  className="w-full"
                  id="ctaLabel"
                  name="ctaLabel"
                  // Vacío, la app muestra "Usar beneficio".
                  placeholder="Ej: Usar beneficio"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.ctaLabel}
                  disabled={isSubmitting}
                />
              </FormField>
              {/* Siempre visible para que la fila quede pareja; se habilita con
                  el método "Código promocional", que es el que lo muestra. */}
              <FormField
                label="Código promocional"
                htmlFor="promoCode"
                error={errorText("promoCode")}
              >
                <InputText
                  className={`w-full ${errorText("promoCode") ? "p-invalid" : ""}`}
                  id="promoCode"
                  name="promoCode"
                  placeholder={
                    values.redemptionMethods.includes("promo_code")
                      ? "Ej: GRUPS20"
                      : "Elegí el método Código promocional"
                  }
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.promoCode}
                  disabled={
                    isSubmitting || !values.redemptionMethods.includes("promo_code")
                  }
                />
              </FormField>
              <FormField label="Imagen de canje (opcional)" span="full">
                {redemptionPreviewSrc && (
                  <img
                    src={redemptionPreviewSrc}
                    alt="Imagen de canje"
                    style={{
                      width: "160px",
                      height: "160px",
                      objectFit: "cover",
                      borderRadius: "8px",
                      border: "1px solid var(--surface-border)",
                      display: "block",
                    }}
                  />
                )}
                <div className="flex flex-wrap align-items-center gap-3">
                  <Button
                    type="button"
                    label={redemptionPreviewSrc ? "Cambiar imagen de canje" : "Subir imagen de canje"}
                    icon="pi pi-upload"
                    outlined
                    onClick={() => redemptionInputRef.current?.click()}
                    disabled={isSubmitting}
                  />
                  {redemptionPreviewSrc && (
                    <Button
                      type="button"
                      label="Quitar"
                      icon="pi pi-times"
                      severity="secondary"
                      outlined
                      onClick={() =>
                        // Primero descarta la nueva sin guardar; si no hay, quita la guardada.
                        redemptionFile
                          ? setRedemptionFile(null)
                          : setRemoveSavedRedemptionImage(true)
                      }
                      disabled={isSubmitting}
                    />
                  )}
                  <small className="text-600">
                    {redemptionFile
                      ? `Pendiente de guardar: ${redemptionFile.name}`
                      : removeSavedRedemptionImage && !redemptionPreviewSrc
                        ? "La imagen se quita al guardar"
                        : "Se muestra al usar el beneficio (por ej. un QR) y reemplaza al QR dinámico · PNG, JPG o WebP · máx. 5 MB"}
                  </small>
                </div>
                <input
                  ref={redemptionInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  style={{ display: "none" }}
                  onChange={handleFileSelect(setRedemptionFile)}
                />
              </FormField>
            </>
          )}
        </FormRow>
      </FormSection>

      {/* ---- Condiciones y vigencia ---- */}
      <FormSection title="Condiciones y vigencia">
        <p className="mt-0 mb-4 text-600 line-height-3">
          Inicio y fin de vigencia definen desde y hasta qué día se publica el
          beneficio (el fin es inclusive). Si los dejás vacíos, no tiene límite.
        </p>
        <FormRow columns={2}>
          <FormField label="Inicio de vigencia" htmlFor="startDate">
            <Calendar
              className="w-full"
              locale={CALENDAR_LOCALE}
              inputId="startDate"
              name="startDate"
              value={values.startDate}
              onChange={(e) => setFieldValue("startDate", e.value ?? null)}
              onBlur={handleBlur}
              showIcon
              showButtonBar
              dateFormat="dd/mm/yy"
              placeholder="Sin límite"
              disabled={isSubmitting}
            />
          </FormField>
          <FormField
            label="Fin de vigencia"
            htmlFor="endDate"
            error={errors.endDate as string | undefined}
          >
            <Calendar
              className="w-full"
              locale={CALENDAR_LOCALE}
              inputId="endDate"
              name="endDate"
              value={values.endDate}
              onChange={(e) => setFieldValue("endDate", e.value ?? null)}
              onBlur={handleBlur}
              showIcon
              showButtonBar
              dateFormat="dd/mm/yy"
              placeholder="Sin límite"
              invalid={!!errors.endDate}
              disabled={isSubmitting}
            />
          </FormField>
          <FormField label="Términos y condiciones" htmlFor="termsAndConditions" span="full">
            <InputTextarea
              className="w-full"
              id="termsAndConditions"
              name="termsAndConditions"
              placeholder="Ej: Válido de lunes a jueves. No acumulable con otras promociones."
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.termsAndConditions}
              rows={4}
              autoResize
              disabled={isSubmitting}
            />
          </FormField>
        </FormRow>
      </FormSection>

      {/* ---- Validez ---- */}
      {/* Reglas de "¿se puede usar hoy?" (computeValidToday en worker-api,
          horario de Argentina). No ocultan el beneficio: fuera de la ventana la
          app lo muestra atenuado como "No disponible hoy". La publicación
          (desde/hasta qué fecha existe) vive en "Condiciones y vigencia". */}
      <FormSection title="Validez">
        <p className="mt-0 mb-4 text-600 line-height-3">
          Definí qué días y en qué horario se puede usar el beneficio. Fuera de
          esos días u horarios sigue visible en la app, pero aparece como{" "}
          <strong>"No disponible hoy"</strong>. Sin días elegidos vale todos los
          días; sin horario, de 00:00 a 23:59. El horario es de Argentina, el
          "hasta" es inclusive y no cruza la medianoche (tiene que ser igual o
          posterior al "desde").
        </p>
        <FormRow columns={3}>
          <FormField label="Días de la semana" htmlFor="validDays">
            <MultiSelect
              className="w-full"
              inputId="validDays"
              value={values.validDays}
              options={benefitValidDayOptions}
              onChange={(e) => setFieldValue("validDays", e.value)}
              optionLabel="label"
              optionValue="value"
              display="chip"
              placeholder="Todos los días"
              disabled={isSubmitting}
              showClear
            />
          </FormField>
          <FormField label="Horario desde" htmlFor="validHoursStart">
            <Calendar
              className="w-full"
              locale={CALENDAR_LOCALE}
              inputId="validHoursStart"
              value={values.validHoursStart}
              onChange={(e) => setFieldValue("validHoursStart", e.value ?? null)}
              onBlur={handleBlur}
              timeOnly
              hourFormat="24"
              placeholder="00:00"
              showButtonBar
              todayButtonClassName="hidden"
              disabled={isSubmitting}
            />
          </FormField>
          <FormField
            label="Horario hasta"
            htmlFor="validHoursEnd"
            error={errors.validHoursEnd as string | undefined}
          >
            <Calendar
              className="w-full"
              locale={CALENDAR_LOCALE}
              inputId="validHoursEnd"
              value={values.validHoursEnd}
              onChange={(e) => setFieldValue("validHoursEnd", e.value ?? null)}
              onBlur={handleBlur}
              timeOnly
              hourFormat="24"
              placeholder="23:59"
              showButtonBar
              todayButtonClassName="hidden"
              invalid={Boolean(errors.validHoursEnd)}
              disabled={isSubmitting}
            />
          </FormField>
        </FormRow>
      </FormSection>

      {/* ---- Imágenes ---- */}
      <FormSection title="Imágenes">
        <BenefitImagesManager
          benefit={benefitData}
          pendingFiles={values.images}
          onPendingFilesChange={(files) => setFieldValue("images", files)}
          disabled={isSubmitting}
        />
        {errorText("images") && (
          <small style={{ color: "var(--error-default)" }}>{errorText("images")}</small>
        )}
      </FormSection>
      </FormSections>

      <FormActions
        align="end"
        primaryLabel={isEdit ? "Guardar cambios" : "Crear Beneficio"}
        loading={isSubmitting}
        disabled={!isEdit && !tenantGate.allowed}
        disabledReason={tenantGate.reason}
        onCancel={() => navigate("/benefits-catalog")}
      />
    </form>
  );
};
